// The PDF pane's zoom (owner, 2026-10-09 — docs/decisions.md D-337: "in the
// invoice modal everywhere, can the pdf preview be zoomed by default to fit the
// width of the document, so that previewer does not show the horizontal scroll
// bar unless the user zooms-in himself?"). Kept out of PdfPane.tsx so the
// component file exports components only, and out of pdfRender.ts so importing
// it does not pull PDF.js into the main bundle.
//
// A ZOOM IS EITHER 'fit' OR A SCALE. 'fit' is the default: the widest shown
// page fills the pane's width, and the scale is worked out again whenever the
// pane changes width. A number is a scale the reviewer chose with Zoom In or
// Zoom Out, and it stays put when the pane resizes. Fit to Width returns to
// 'fit'.
//
// 100% IS THE PAGE'S PRINTED SIZE: an 8.5-inch page is 8.5 inches wide on the
// screen, counted at 96 CSS pixels per inch as PDF viewers count it. Until
// 2026-10-09, 100% meant "as wide as the pane", so every zoom level was a share
// of the pane and the readout said 100% whatever size the page was drawn at.

/** CSS pixels per PDF point: 96 per inch over 72 per inch. */
export const CSS_PX_PER_PDF_POINT = 96 / 72

export const ZOOM_MIN = 0.25
export const ZOOM_MAX = 3
/** Zoom In and Zoom Out move to the next multiple of this, up or down. */
const ZOOM_STEP = 0.25

/** `'fit'` (the default) or a scale, where 1 is the page's printed size. */
export type PdfZoom = 'fit' | number

/**
 * The scale at which the widest page exactly fills `contentWidth` CSS pixels.
 * `widestPagePoints` is that page's width in PDF points at the turn shown. Not
 * clamped to ZOOM_MIN..ZOOM_MAX: fitting the width is the point, so a very
 * wide or very narrow pane still fits.
 */
export function fitScale(contentWidth: number, widestPagePoints: number): number {
  if (!(widestPagePoints > 0) || !(contentWidth > 0)) return 1
  return contentWidth / (widestPagePoints * CSS_PX_PER_PDF_POINT)
}

const round2 = (n: number) => Math.round(n * 100) / 100
// A scale already on a step (1.0, 1.25) moves a whole step, not to itself.
const EPSILON = 1e-6

const clampZoom = (n: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n))

/** Zoom In: the next multiple of ZOOM_STEP above `scale`, within ZOOM_MIN..ZOOM_MAX. */
export function zoomIn(scale: number): number {
  return clampZoom(round2((Math.floor(scale / ZOOM_STEP + EPSILON) + 1) * ZOOM_STEP))
}

/**
 * Zoom Out: the next multiple of ZOOM_STEP below `scale`, within
 * ZOOM_MIN..ZOOM_MAX — so a pane wide enough to fit above 300% zooms out to
 * 300%, not to a step above the ceiling.
 */
export function zoomOut(scale: number): number {
  return clampZoom(round2((Math.ceil(scale / ZOOM_STEP - EPSILON) - 1) * ZOOM_STEP))
}

/**
 * The readout between the zoom buttons: the scale in force as a whole
 * percentage, the fitted one included. "Fit" only in the moment before the
 * view has measured the pane.
 */
export function zoomReadout(zoom: PdfZoom, fitted: number | null): string {
  const scale = zoom === 'fit' ? fitted : zoom
  return scale === null ? 'Fit' : `${Math.round(scale * 100)}%`
}
